import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { SkeletonCard } from "@/components/skeleton";
import { formatTime } from "@/lib/i18n";

/**
 * G.18: Webinar AI Q&A Assistant
 * Split view: Live Q&A panel (left) + AI Suggestions panel (right)
 * Post-webinar: unanswered questions + FAQ generation
 */

interface Question {
  id: string;
  contactId: string;
  question: string;
  answer: string | null;
  upvotes: number;
  aiSuggestedAnswer: string | null;
  createdAt: string;
}

interface Suggestion {
  suggestionId: string;
  questionId: string;
  suggestedAnswer: string;
  confidence: number;
  sources: Array<{ type: string; title: string; excerpt: string }>;
  status: "pending" | "accepted" | "dismissed";
}

interface DraftAnswer {
  questionId: string;
  questionText: string;
  draftAnswer: string;
  confidence: number;
}

interface FaqEntry {
  question: string;
  answer: string;
  frequency: number;
}

type ActiveTab = "live" | "post-webinar";

export default function QaAssistantPage() {
  const { webinarId } = useParams();
  const [activeTab, setActiveTab] = useState<ActiveTab>("live");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [unanswered, setUnanswered] = useState<Question[]>([]);
  const [suggestions, setSuggestions] = useState<Map<string, Suggestion>>(new Map());
  const [drafts, setDrafts] = useState<DraftAnswer[]>([]);
  const [faq, setFaq] = useState<FaqEntry[]>([]);
  const [remaining, setRemaining] = useState(30);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isDraftingAll, setIsDraftingAll] = useState(false);
  const [isGeneratingFaq, setIsGeneratingFaq] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState<{ suggestionId: string; text: string } | null>(null);
  const [requestingSuggestion, setRequestingSuggestion] = useState<string | null>(null);

  // Fetch questions
  const fetchQuestions = useCallback(async () => {
    if (!webinarId) return;
    try {
      const res = await apiFetch<{ data: Question[] }>(
        `/v1/webinars/${webinarId}/questions`,
      );
      setQuestions(res.data);
    } catch {
      // Will retry on next poll
    } finally {
      setIsLoadingQuestions(false);
    }
  }, [webinarId]);

  // Fetch unanswered questions
  const fetchUnanswered = useCallback(async () => {
    if (!webinarId) return;
    try {
      const res = await apiFetch<{ data: Question[] }>(
        `/v1/webinars/${webinarId}/qa/unanswered`,
      );
      setUnanswered(res.data);
    } catch {
      // Handled by error state
    }
  }, [webinarId]);

  useEffect(() => {
    fetchQuestions();
    const interval = setInterval(fetchQuestions, 5000);
    return () => clearInterval(interval);
  }, [fetchQuestions]);

  useEffect(() => {
    if (activeTab === "post-webinar") {
      fetchUnanswered();
    }
  }, [activeTab, fetchUnanswered]);

  // Request AI suggestion for a question
  const handleRequestSuggestion = async (questionId: string, questionText: string) => {
    if (!webinarId || requestingSuggestion) return;
    setRequestingSuggestion(questionId);

    try {
      const res = await apiFetch<{
        data: {
          suggestionId: string;
          suggestedAnswer: string;
          confidence: number;
          sources: Array<{ type: string; title: string; excerpt: string }>;
        };
        remaining: number;
      }>(`/v1/webinars/${webinarId}/qa/suggest`, {
        method: "POST",
        body: JSON.stringify({ questionId, questionText }),
      });

      setRemaining(res.remaining);
      setSuggestions((prev) => {
        const next = new Map(prev);
        next.set(res.data.suggestionId, {
          ...res.data,
          questionId,
          status: "pending",
        });
        return next;
      });
    } catch {
      // Error handled by apiFetch
    } finally {
      setRequestingSuggestion(null);
    }
  };

  // Accept suggestion
  const handleAccept = async (suggestionId: string, editedAnswer?: string) => {
    if (!webinarId) return;
    try {
      await apiFetch(`/v1/webinars/${webinarId}/qa/suggest/${suggestionId}/accept`, {
        method: "POST",
        body: JSON.stringify({ editedAnswer }),
      });

      setSuggestions((prev) => {
        const next = new Map(prev);
        const s = next.get(suggestionId);
        if (s) next.set(suggestionId, { ...s, status: "accepted" });
        return next;
      });
      setEditingAnswer(null);
      fetchQuestions();
    } catch {
      // Error handled by apiFetch
    }
  };

  // Dismiss suggestion
  const handleDismiss = async (suggestionId: string) => {
    if (!webinarId) return;
    try {
      await apiFetch(`/v1/webinars/${webinarId}/qa/suggest/${suggestionId}/dismiss`, {
        method: "POST",
      });

      setSuggestions((prev) => {
        const next = new Map(prev);
        const s = next.get(suggestionId);
        if (s) next.set(suggestionId, { ...s, status: "dismissed" });
        return next;
      });
    } catch {
      // Error handled by apiFetch
    }
  };

  // Draft all answers
  const handleDraftAll = async () => {
    if (!webinarId || isDraftingAll) return;
    setIsDraftingAll(true);
    try {
      const res = await apiFetch<{ data: { drafts: DraftAnswer[] } }>(
        `/v1/webinars/${webinarId}/qa/draft-answers`,
        { method: "POST" },
      );
      setDrafts(res.data.drafts);
    } catch {
      // Error handled by apiFetch
    } finally {
      setIsDraftingAll(false);
    }
  };

  // Generate FAQ
  const handleGenerateFaq = async () => {
    if (!webinarId || isGeneratingFaq) return;
    setIsGeneratingFaq(true);
    try {
      const res = await apiFetch<{
        data: { faq: FaqEntry[]; remaining: number };
      }>(`/v1/webinars/${webinarId}/qa/faq`);
      setFaq(res.data.faq);
      setRemaining(res.data.remaining);
    } catch {
      // Error handled by apiFetch
    } finally {
      setIsGeneratingFaq(false);
    }
  };

  // Get pending suggestions for a question
  const getSuggestionsForQuestion = (questionId: string): Suggestion[] => {
    const results: Suggestion[] = [];
    for (const s of suggestions.values()) {
      if (s.questionId === questionId && s.status === "pending") {
        results.push(s);
      }
    }
    return results;
  };

  if (isLoadingQuestions) {
    return (
      <div className="grid min-h-screen grid-cols-1 gap-4 p-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <SkeletonCard key={index} className="h-[320px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">AI Q&A Assistant</h1>
          <p className="text-xs text-muted-foreground">
            {remaining}/{30} AI suggestions remaining
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          <button
            onClick={() => setActiveTab("live")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === "live"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Live Q&A
          </button>
          <button
            onClick={() => setActiveTab("post-webinar")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === "post-webinar"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Post-Webinar
          </button>
        </div>
      </div>

      {activeTab === "live" ? (
        <LiveQaView
          questions={questions}
          suggestions={suggestions}
          remaining={remaining}
          requestingSuggestion={requestingSuggestion}
          editingAnswer={editingAnswer}
          onRequestSuggestion={handleRequestSuggestion}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
          onStartEdit={(suggestionId, text) => setEditingAnswer({ suggestionId, text })}
          onEditChange={(text) => setEditingAnswer((prev) => prev ? { ...prev, text } : null)}
          onCancelEdit={() => setEditingAnswer(null)}
          getSuggestionsForQuestion={getSuggestionsForQuestion}
        />
      ) : (
        <PostWebinarView
          unanswered={unanswered}
          drafts={drafts}
          faq={faq}
          isDraftingAll={isDraftingAll}
          isGeneratingFaq={isGeneratingFaq}
          onDraftAll={handleDraftAll}
          onGenerateFaq={handleGenerateFaq}
        />
      )}
    </div>
  );
}

// --- Live Q&A View ---

interface LiveQaViewProps {
  questions: Question[];
  suggestions: Map<string, Suggestion>;
  remaining: number;
  requestingSuggestion: string | null;
  editingAnswer: { suggestionId: string; text: string } | null;
  onRequestSuggestion: (questionId: string, questionText: string) => void;
  onAccept: (suggestionId: string, editedAnswer?: string) => void;
  onDismiss: (suggestionId: string) => void;
  onStartEdit: (suggestionId: string, text: string) => void;
  onEditChange: (text: string) => void;
  onCancelEdit: () => void;
  getSuggestionsForQuestion: (questionId: string) => Suggestion[];
}

function LiveQaView({
  questions,
  remaining,
  requestingSuggestion,
  editingAnswer,
  onRequestSuggestion,
  onAccept,
  onDismiss,
  onStartEdit,
  onEditChange,
  onCancelEdit,
  getSuggestionsForQuestion,
}: LiveQaViewProps) {
  return (
    <div className="grid h-[calc(100vh-60px)] grid-cols-1 gap-4 p-4 lg:grid-cols-2">
      {/* Left: Question list */}
      <div className="flex flex-col overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-muted px-4 py-2">
          <h2 className="text-sm font-medium text-foreground">
            Questions ({questions.length})
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {questions.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No questions yet. Questions from attendees will appear here.
            </p>
          ) : (
            <div className="space-y-2">
              {questions.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  isRequesting={requestingSuggestion === q.id}
                  hasRemainingQuota={remaining > 0}
                  suggestions={getSuggestionsForQuestion(q.id)}
                  editingAnswer={editingAnswer}
                  onRequestSuggestion={onRequestSuggestion}
                  onAccept={onAccept}
                  onDismiss={onDismiss}
                  onStartEdit={onStartEdit}
                  onEditChange={onEditChange}
                  onCancelEdit={onCancelEdit}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: AI Suggestions panel */}
      <div className="flex flex-col overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-muted px-4 py-2">
          <h2 className="text-sm font-medium text-foreground">AI Suggestions</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <SuggestionsList
            questions={questions}
            getSuggestionsForQuestion={getSuggestionsForQuestion}
            editingAnswer={editingAnswer}
            onAccept={onAccept}
            onDismiss={onDismiss}
            onStartEdit={onStartEdit}
            onEditChange={onEditChange}
            onCancelEdit={onCancelEdit}
          />
        </div>
      </div>
    </div>
  );
}

// --- Question Card ---

interface QuestionCardProps {
  question: Question;
  isRequesting: boolean;
  hasRemainingQuota: boolean;
  suggestions: Suggestion[];
  editingAnswer: { suggestionId: string; text: string } | null;
  onRequestSuggestion: (questionId: string, questionText: string) => void;
  onAccept: (suggestionId: string, editedAnswer?: string) => void;
  onDismiss: (suggestionId: string) => void;
  onStartEdit: (suggestionId: string, text: string) => void;
  onEditChange: (text: string) => void;
  onCancelEdit: () => void;
}

function QuestionCard({
  question,
  isRequesting,
  hasRemainingQuota,
  suggestions,
  editingAnswer,
  onRequestSuggestion,
  onAccept,
  onDismiss,
  onStartEdit,
  onEditChange,
  onCancelEdit,
}: QuestionCardProps) {
  const isAnswered = question.answer !== null;

  return (
    <div className="stagger-item rounded-lg border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-sm text-foreground">{question.question}</p>
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            <span>{question.upvotes} upvotes</span>
            <span>{formatTime(question.createdAt)}</span>
          </div>
        </div>
        {isAnswered ? (
          <span className="shrink-0 rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
            Answered
          </span>
        ) : (
          <button
            onClick={() => onRequestSuggestion(question.id, question.question)}
            disabled={isRequesting || !hasRemainingQuota}
            className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isRequesting ? "Thinking..." : "Get AI Suggestion"}
          </button>
        )}
      </div>

      {isAnswered && question.answer && (
        <div className="mt-2 rounded-md bg-accent p-2">
          <p className="text-xs font-medium text-muted-foreground">Answer:</p>
          <p className="text-sm text-foreground">{question.answer}</p>
        </div>
      )}

      {/* Inline suggestions for this question */}
      {suggestions.length > 0 && (
        <div className="mt-2 space-y-2">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.suggestionId}
              suggestion={s}
              editingAnswer={editingAnswer}
              onAccept={onAccept}
              onDismiss={onDismiss}
              onStartEdit={onStartEdit}
              onEditChange={onEditChange}
              onCancelEdit={onCancelEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Suggestion Card ---

interface SuggestionCardProps {
  suggestion: Suggestion;
  editingAnswer: { suggestionId: string; text: string } | null;
  onAccept: (suggestionId: string, editedAnswer?: string) => void;
  onDismiss: (suggestionId: string) => void;
  onStartEdit: (suggestionId: string, text: string) => void;
  onEditChange: (text: string) => void;
  onCancelEdit: () => void;
}

function SuggestionCard({
  suggestion,
  editingAnswer,
  onAccept,
  onDismiss,
  onStartEdit,
  onEditChange,
  onCancelEdit,
}: SuggestionCardProps) {
  const isEditing = editingAnswer?.suggestionId === suggestion.suggestionId;
  const confidenceColor =
    suggestion.confidence > 0.8
      ? "bg-[var(--color-success)]/50"
      : suggestion.confidence >= 0.5
        ? "bg-[var(--color-warning)]/50"
        : "bg-[var(--color-destructive)]/50";

  const confidenceLabel =
    suggestion.confidence > 0.8
      ? "High"
      : suggestion.confidence >= 0.5
        ? "Medium"
        : "Low";

  return (
    <div className="rounded-md border border-border bg-muted p-3">
      {/* Confidence indicator */}
      <div className="mb-2 flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <div className={`h-2 w-2 rounded-full ${confidenceColor}`} />
          <span className="text-xs font-medium text-muted-foreground">
            {confidenceLabel} confidence ({Math.round(suggestion.confidence * 100)}%)
          </span>
        </div>
        <div className="ml-auto h-1.5 w-24 overflow-hidden rounded-full bg-border">
          <div
            className={`h-full rounded-full ${confidenceColor}`}
            style={{ width: `${Math.round(suggestion.confidence * 100)}%` }}
          />
        </div>
      </div>

      {/* Answer text or editor */}
      {isEditing ? (
        <textarea
          value={editingAnswer.text}
          onChange={(e) => onEditChange(e.target.value)}
          className="mb-2 w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground"
          rows={4}
        />
      ) : (
        <p className="mb-2 text-sm text-foreground">{suggestion.suggestedAnswer}</p>
      )}

      {/* Sources */}
      {suggestion.sources.length > 0 && (
        <div className="mb-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">Sources:</p>
          <div className="space-y-1">
            {suggestion.sources.map((source, i) => (
              <div
                key={i}
                className="rounded border border-border bg-background px-2 py-1"
              >
                <span className="text-xs font-medium text-foreground">
                  [{source.type}] {source.title}
                </span>
                <p className="text-xs text-muted-foreground">{source.excerpt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {isEditing ? (
          <>
            <button
              onClick={() => onAccept(suggestion.suggestionId, editingAnswer.text)}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Save & Use
            </button>
            <button
              onClick={onCancelEdit}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => onAccept(suggestion.suggestionId)}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Use
            </button>
            <button
              onClick={() => onStartEdit(suggestion.suggestionId, suggestion.suggestedAnswer)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              Edit & Use
            </button>
            <button
              onClick={() => onDismiss(suggestion.suggestionId)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent"
            >
              Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Suggestions List (right panel) ---

interface SuggestionsListProps {
  questions: Question[];
  getSuggestionsForQuestion: (questionId: string) => Suggestion[];
  editingAnswer: { suggestionId: string; text: string } | null;
  onAccept: (suggestionId: string, editedAnswer?: string) => void;
  onDismiss: (suggestionId: string) => void;
  onStartEdit: (suggestionId: string, text: string) => void;
  onEditChange: (text: string) => void;
  onCancelEdit: () => void;
}

function SuggestionsList({
  questions,
  getSuggestionsForQuestion,
  editingAnswer,
  onAccept,
  onDismiss,
  onStartEdit,
  onEditChange,
  onCancelEdit,
}: SuggestionsListProps) {
  const allSuggestions: Array<{ question: Question; suggestion: Suggestion }> = [];
  for (const q of questions) {
    for (const s of getSuggestionsForQuestion(q.id)) {
      allSuggestions.push({ question: q, suggestion: s });
    }
  }

  if (allSuggestions.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="mb-3 text-4xl">?</div>
        <p className="text-sm font-medium text-foreground">No suggestions yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Click &quot;Get AI Suggestion&quot; on any question to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {allSuggestions.map(({ question, suggestion }) => (
        <div key={suggestion.suggestionId}>
          <p className="mb-1 truncate text-xs font-medium text-muted-foreground">
            Q: {question.question}
          </p>
          <SuggestionCard
            suggestion={suggestion}
            editingAnswer={editingAnswer}
            onAccept={onAccept}
            onDismiss={onDismiss}
            onStartEdit={onStartEdit}
            onEditChange={onEditChange}
            onCancelEdit={onCancelEdit}
          />
        </div>
      ))}
    </div>
  );
}

// --- Post-Webinar View ---

interface PostWebinarViewProps {
  unanswered: Question[];
  drafts: DraftAnswer[];
  faq: FaqEntry[];
  isDraftingAll: boolean;
  isGeneratingFaq: boolean;
  onDraftAll: () => void;
  onGenerateFaq: () => void;
}

function PostWebinarView({
  unanswered,
  drafts,
  faq,
  isDraftingAll,
  isGeneratingFaq,
  onDraftAll,
  onGenerateFaq,
}: PostWebinarViewProps) {
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Unanswered Questions */}
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-medium text-foreground">
              Unanswered Questions ({unanswered.length})
            </h2>
            <p className="text-xs text-muted-foreground">
              Questions that were not answered during the webinar
            </p>
          </div>
          {unanswered.length > 0 && (
            <button
              onClick={onDraftAll}
              disabled={isDraftingAll}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isDraftingAll ? "Drafting answers..." : "Draft All Answers"}
            </button>
          )}
        </div>
        <div className="divide-y divide-border">
          {unanswered.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              All questions have been answered.
            </p>
          ) : (
            unanswered.map((q) => {
              const draft = drafts.find((d) => d.questionId === q.id);
              return (
                <div key={q.id} className="stagger-item p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{q.question}</p>
                      <span className="text-xs text-muted-foreground">
                        {q.upvotes} upvotes
                      </span>
                    </div>
                  </div>
                  {draft && (
                    <div className="mt-2 rounded-md bg-accent p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          AI Draft
                        </span>
                        <ConfidenceBadge confidence={draft.confidence} />
                      </div>
                      <p className="text-sm text-foreground">{draft.draftAnswer}</p>
                    </div>
                  )}
                  {!draft && q.aiSuggestedAnswer && (
                    <div className="mt-2 rounded-md bg-accent p-3">
                      <span className="text-xs font-medium text-muted-foreground">
                        Previous AI Suggestion
                      </span>
                      <p className="text-sm text-foreground">{q.aiSuggestedAnswer}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* FAQ Generation */}
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-medium text-foreground">Generated FAQ</h2>
            <p className="text-xs text-muted-foreground">
              Similar questions grouped and ranked by frequency
            </p>
          </div>
          <button
            onClick={onGenerateFaq}
            disabled={isGeneratingFaq}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            {isGeneratingFaq ? "Generating..." : "Generate FAQ"}
          </button>
        </div>
        <div className="divide-y divide-border">
          {faq.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              Click &quot;Generate FAQ&quot; to create a FAQ from webinar questions.
            </p>
          ) : (
            faq.map((entry, i) => (
              <div key={i} className="stagger-item p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{entry.question}</p>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    Asked {entry.frequency}x
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{entry.answer}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// --- Confidence Badge ---

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const color =
    confidence > 0.8
      ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
      : confidence >= 0.5
        ? "bg-[var(--color-warning)]/10 text-yellow-800"
        : "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";

  const label =
    confidence > 0.8
      ? "High"
      : confidence >= 0.5
        ? "Medium"
        : "Low";

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {label} ({Math.round(confidence * 100)}%)
    </span>
  );
}
